export const SELF_HELP_FEEDBACK_CATEGORIES = [
  "stepsDidNotResolveIssue",
  "couldNotCompleteStep",
  "instructionsDidNotMatchExperience",
  "receivedErrorMessage",
  "somethingElse",
] as const;

export type SelfHelpFeedbackCategory =
  (typeof SELF_HELP_FEEDBACK_CATEGORIES)[number];

export type SelfHelpFeedbackJourneyStatus =
  | "resolution"
  | "category"
  | "feedback"
  | "validating"
  | "revision"
  | "fallback"
  | "ticket-ready"
  | "resolved"
  | "abandoned";

export interface SelfHelpFeedbackDescriptor {
  descriptorId: string;
  owningSelfHelpTurnId: string;
}

interface SelfHelpFeedbackJourneyIdentity extends SelfHelpFeedbackDescriptor {
  journeyId: string;
}

interface CategorizedSelfHelpFeedbackJourney
  extends SelfHelpFeedbackJourneyIdentity {
  category: SelfHelpFeedbackCategory;
}

interface SubmittedSelfHelpFeedbackJourney
  extends CategorizedSelfHelpFeedbackJourney {
  feedback: string;
  validatorGuidance?: string;
}

export interface SelfHelpFeedbackResolutionState
  extends SelfHelpFeedbackJourneyIdentity {
  status: "resolution";
}

export interface SelfHelpFeedbackCategoryState
  extends SelfHelpFeedbackJourneyIdentity {
  status: "category";
}

export interface SelfHelpFeedbackEntryState
  extends CategorizedSelfHelpFeedbackJourney {
  status: "feedback";
  error?: string;
}

export interface SelfHelpFeedbackValidatingState
  extends SubmittedSelfHelpFeedbackJourney {
  status: "validating";
}

export interface SelfHelpFeedbackRevisionState
  extends SubmittedSelfHelpFeedbackJourney {
  status: "revision";
  validatorGuidance: string;
  error?: string;
}

export interface SelfHelpFeedbackFallbackState
  extends SubmittedSelfHelpFeedbackJourney {
  status: "fallback";
  fallbackMessage: string;
}

export interface SelfHelpFeedbackTicketReadyState
  extends SubmittedSelfHelpFeedbackJourney {
  status: "ticket-ready";
  primaryFailureReason?: import("./api").SelfHelpFeedbackPrimaryFailureReason;
  guidanceDisposition?: import("./api").SelfHelpGuidanceDisposition;
  failedStepNumber?: number;
}

export interface SelfHelpFeedbackResolvedState
  extends SelfHelpFeedbackJourneyIdentity {
  status: "resolved";
}

export interface SelfHelpFeedbackAbandonedState
  extends SelfHelpFeedbackJourneyIdentity {
  status: "abandoned";
}

export type SelfHelpFeedbackState =
  | SelfHelpFeedbackResolutionState
  | SelfHelpFeedbackCategoryState
  | SelfHelpFeedbackEntryState
  | SelfHelpFeedbackValidatingState
  | SelfHelpFeedbackRevisionState
  | SelfHelpFeedbackFallbackState
  | SelfHelpFeedbackTicketReadyState
  | SelfHelpFeedbackResolvedState
  | SelfHelpFeedbackAbandonedState;

export type SelfHelpFeedbackAction =
  | { type: "answerResolution"; resolved: boolean }
  | { type: "selectCategory"; category: SelfHelpFeedbackCategory }
  | { type: "submitFeedback"; feedback: string; requiredError: string }
  | { type: "validationNeedsImprovement"; guidance: string }
  | {
      type: "validationPassed";
      primaryFailureReason?: import("./api").SelfHelpFeedbackPrimaryFailureReason;
      guidanceDisposition?: import("./api").SelfHelpGuidanceDisposition;
      failedStepNumber?: number;
    }
  | { type: "validationFailed"; message: string }
  | { type: "complete" }
  | { type: "abandon" };

// Generated by Copilot
// updated on: 2026-09-09 | Story 1297509 TASK-002 | Added typed self-help feedback journey contracts | Time Taken 5 min
// updated on: 2026-09-10 | Story 1297509 hardening | Routed required-field errors through descriptor copy | Time Taken 1 min
// updated on: 2026-09-14 | 8 lines changed | [Auto Generated] | Preserve typed primary failure reason in ticket-ready state | Time Taken 2 min
// updated on: 2026-10-18 | 1 line added | [Auto Generated] | Added terminal completion action for successful ticket handoff | Time Taken 1 min
